/**
 * Utility for loading images with proper EXIF orientation handling
 *
 * Problem: When drawing images to canvas with ctx.drawImage(), EXIF orientation
 * metadata handling is inconsistent across browsers:
 * - Chrome 81+: Usually applies EXIF
 * - Safari iOS: Has known bugs with EXIF handling
 * - Firefox: Behavior varies by version
 * - Older browsers: Don't apply EXIF at all
 *
 * Solution: Use createImageBitmap with explicit imageOrientation option
 * which provides standardized, reliable EXIF handling across all modern browsers.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/createImageBitmap
 * @see https://github.com/whatwg/html/issues/10492
 */

/**
 * Loads an image file and returns an ImageBitmap with EXIF orientation applied
 * This ensures consistent orientation regardless of browser quirks
 *
 * @param file - The image File or Blob to load
 * @returns Promise resolving to an ImageBitmap with correct orientation
 */
export const loadImageBitmapWithOrientation = async (
  file: File | Blob
): Promise<ImageBitmap> => {
  // createImageBitmap with imageOrientation: "from-image" ensures EXIF
  // orientation is applied consistently across all browsers
  return createImageBitmap(file, {
    imageOrientation: "from-image",
  });
};

/**
 * Loads an image from a data URL and returns an ImageBitmap with EXIF orientation applied
 *
 * @param dataUrl - The data URL of the image
 * @returns Promise resolving to an ImageBitmap with correct orientation
 */
export const loadImageBitmapFromDataUrl = async (
  dataUrl: string
): Promise<ImageBitmap> => {
  // Convert data URL to Blob
  const response = await fetch(dataUrl);
  const blob = await response.blob();

  return createImageBitmap(blob, {
    imageOrientation: "from-image",
  });
};

/**
 * Converts a File/Blob to a data URL with EXIF orientation baked in
 * This is useful when you need a data URL for display but want to ensure
 * the orientation is correct for canvas operations
 *
 * @param file - The image File or Blob to convert
 * @returns Promise resolving to a data URL with orientation applied
 */
export const fileToOrientedDataUrl = async (file: File | Blob): Promise<string> => {
  // Load with proper orientation
  const imageBitmap = await loadImageBitmapWithOrientation(file);

  // Draw to canvas to bake in the orientation
  const canvas = document.createElement("canvas");
  canvas.width = imageBitmap.width;
  canvas.height = imageBitmap.height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("No se pudo crear el contexto del canvas");
  }

  ctx.drawImage(imageBitmap, 0, 0);

  // Convert to data URL
  const dataUrl = canvas.toDataURL("image/png");

  // Cleanup
  canvas.width = 0;
  canvas.height = 0;
  imageBitmap.close();

  return dataUrl;
};

/**
 * Draws an image to a canvas with proper EXIF orientation handling
 * This is a drop-in replacement for the pattern:
 *   ctx.drawImage(img, 0, 0)
 *
 * @param ctx - The canvas 2D rendering context
 * @param source - The image source (File, Blob, or data URL string)
 * @returns Promise resolving to the ImageBitmap dimensions { width, height }
 */
export const drawImageWithOrientation = async (
  ctx: CanvasRenderingContext2D,
  source: File | Blob | string
): Promise<{ width: number; height: number }> => {
  let imageBitmap: ImageBitmap;

  if (typeof source === "string") {
    // It's a data URL
    imageBitmap = await loadImageBitmapFromDataUrl(source);
  } else {
    // It's a File or Blob
    imageBitmap = await loadImageBitmapWithOrientation(source);
  }

  // Resize canvas to match the oriented image dimensions
  const canvas = ctx.canvas;
  canvas.width = imageBitmap.width;
  canvas.height = imageBitmap.height;

  // Draw the properly oriented image
  ctx.drawImage(imageBitmap, 0, 0);

  const dimensions = { width: imageBitmap.width, height: imageBitmap.height };

  // Cleanup ImageBitmap to free memory
  imageBitmap.close();

  return dimensions;
};
